import React, { useState, useEffect, useRef } from 'react';
import { Button, Table, message, Progress, Space } from 'antd';

const UserMigration = ({ db }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [migrating, setMigrating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [paused, setPaused] = useState(false);
    const [stopped, setStopped] = useState(false);
    const pausedRef = useRef(false);
    const stoppedRef = useRef(false);

    useEffect(() => { pausedRef.current = paused; }, [paused]);
    useEffect(() => { stoppedRef.current = stopped; }, [stopped]);

    const waitIfPaused = async (pRef, sRef) => {
        while (pRef.current && !sRef.current) {
            await new Promise(resolve => setTimeout(resolve, 500));
        }
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const snapshot = await db.collection('users').get();
            setUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (error) {
            message.error("Failed to fetch users");
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const migrateAllUsers = async () => {
        setMigrating(true);
        setStopped(false);
        setPaused(false);
        stoppedRef.current = false;
        pausedRef.current = false;
        setProgress(0);

        const usersToMigrate = users.filter(user => user.organization);
        const total = usersToMigrate.length;
        if (total === 0) { message.info("No users need migration"); setMigrating(false); return; }

        let processed = 0;
        for (const user of usersToMigrate) {
            if (stoppedRef.current) break;
            await waitIfPaused(pausedRef, stoppedRef);
            if (stoppedRef.current) break;

            try {
                const orgDoc = await db.collection('organizations').doc(user.organization).get();
                if (orgDoc.exists) {
                    const orgData = orgDoc.data();
                    const fullOrgName = `${orgData.company_name || ''} ${orgData.name || ''}`.trim();
                    await db.collection('users').doc(user.id).update({
                        organizations: [{
                            organization: user.organization,
                            name: fullOrgName
                        }]
                    });
                }
            } catch (error) { console.error(error); }
            processed++;
            setProgress(Math.round((processed / total) * 100));
        }
        setMigrating(false);
        message[stoppedRef.current ? 'warning' : 'success'](stoppedRef.current ? "Stopped" : "Completed");
        fetchUsers();
    };

    const columnsUsers = [
        { title: 'User ID', dataIndex: 'id', key: 'id' },
        { title: 'Last Name', dataIndex: 'last_name', key: 'last_name' },
        { title: 'First Name', dataIndex: 'first_name', key: 'first_name' },
        { title: 'Org ID', dataIndex: 'organization', key: 'organization' },
        {
            title: 'Multi Org Status',
            key: 'multi_org',
            render: (_, record) => record.organizations && record.organizations.length > 0 ? <span style={{ color: 'green' }}>Converted</span> : <span style={{ color: 'orange' }}>Pending</span>
        }
    ];

    return (
        <>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>List of users and their migration status</span>
                <Space>
                    {migrating ? (
                        <>
                            <Button onClick={() => setPaused(!paused)}>{paused ? "Resume" : "Pause"}</Button>
                            <Button danger onClick={() => { setStopped(true); stoppedRef.current = true; }}>Stop</Button>
                        </>
                    ) : (
                        <Button type="primary" onClick={migrateAllUsers}>Migrate All</Button>
                    )}
                </Space>
            </div>
            {migrating && <Progress percent={progress} status={paused ? "active" : "normal"} style={{ marginBottom: '16px' }} />}
            <Table dataSource={users} columns={columnsUsers} rowKey="id" loading={loading} pagination={{ pageSize: 15 }} />
        </>
    );
};

export default UserMigration;
